import React, { useState, useMemo, useEffect } from 'react';
import { useToasts } from 'react-toast-notifications';
import { useHistory } from 'react-router-dom';
import { Box, Button } from 'rebass';
import { Input, Label, Textarea } from '@rebass/forms';

import helpers from 'utils/helpers';
import helpersScreener from 'utils/helpers-screener';
import studies_service from 'services/studies';
import people_service from 'services/people';
import Portal from 'components/Portal';
import NiceModal from 'components/NiceModal';
import NiceSelect from 'components/NiceSelect';
import { Combobox } from 'components/Combobox';
import { ActionButton } from 'components/Button/ActionButton';
import CreatePanel from 'components/Participant/CreatePanel';
import PanelSelect from 'components/PanelSelect';
import services from 'services/services';
import QualtricsSurveySelect from 'components/Qualtrics/QualtricsSurveySelect';
import ScreenerIntegrationCustomFields from 'components/ScreenerIntegrationCustomFields';
import { SCREENER_TYPE, SCREENER_INTEGRATION_VENDORS } from 'utils/constants';
import useQuery from 'hooks/useQuery';
import config from 'config';

/**
 * Display a New Screener button.
 *
 * @param {Object} props
 * @param {number} props.studyId study ID
 * @param {Object} props.auth Auth object
 **/
function NewScreener({ studyId, auth }) {
    const { addToast } = useToasts();
    const history = useHistory();
    const [showModal, setShowModal] = useState(false);
    const [showModalCreatePanel, setShowModalCreatePanel] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [panelIdSelected, setPanelIdSelected] = useState(null);
    const [screenerType, setScreenerType] = useState(SCREENER_TYPE.PANELFOX);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [copyFormTypeId, setCopyFormTypeId] = useState(null);
    const [integrationVendor, setIntegrationVendor] = useState(null);
    const [externalId, setExternalId] = useState(null);

    const [customFields, setCustomFields] = useState([]);
    const isQualtrics = screenerType === SCREENER_TYPE.QUALTRICS;

    const { data: panels, refetch: refetchPanels } = useQuery({
        queryFn: () => people_service.getPanels(),
        onSuccess: data => {
            if (data.length && !panelIdSelected) {
                setPanelIdSelected(data[0].id);
            }
        },
        onError: (_, errorText) => {
            addToast(errorText, {
                appearance: 'error',
                autoDismiss: true
            });
        }
    });
    const { data: allForms } = useQuery({
        queryFn: () => studies_service.getAllForms(),
        onError: (_, errorText) => {
            addToast(errorText, {
                appearance: 'error',
                autoDismiss: true
            });
        }
    });
    // Fetch panel properties
    const { data: panelProperties } = useQuery({
        queryFn: () => services.getCustomDataColumnsXHR()
    });

    const screenerTypes = [{ id: SCREENER_TYPE.PANELFOX, title: 'Private Panels Screener', icon: '' }];
    if (helpers.hasQualtricsIntegration(auth)) {
        screenerTypes.push({
            id: SCREENER_TYPE.QUALTRICS,
            title: 'Qualtrics Survey',
            icon: <img src={`${config.UI_SUFFIX}/qualtrics-16x16.svg`} alt="Qualtrics" style={{ marginRight: '6px' }} />
        });
    }

    const allCustomFieldsValid = helpersScreener.areCustomFieldsValid(customFields);
    const createDisabled =
        isSubmitting ||
        (isQualtrics && !externalId) ||
        (isQualtrics && customFields.length > 0 && !allCustomFieldsValid);

    function saveFormXHR(e) {
        e.preventDefault();

        setIsSubmitting(true);

        studies_service
            .createScreener(
                studyId,
                title,
                description,
                copyFormTypeId,
                panelIdSelected,
                integrationVendor,
                externalId,
                customFields
            )
            .then(screener_response => {
                addToast('Created', {
                    appearance: 'success',
                    autoDismiss: true
                });

                history.push(`/studies/${studyId}/screeners/${screener_response.id}`);
            })
            .catch(error => {
                setIsSubmitting(false);
                const errorText = services.parseAndTrackXhrErrors(error);
                addToast(errorText, {
                    appearance: 'error',
                    autoDismiss: true
                });
            });
    }

    const getCopyDropdownItems = useMemo(() => {
        const items = [];
        items.push({
            value: 0,
            label: 'Do not copy'
        });

        allForms?.forEach(form => {
            items.push({
                value: form.type_id,
                label: form.source_title
            });
        });

        return items;
    }, [allForms]);

    function getScreenerTypeValueMap() {
        const items = {};
        screenerTypes.forEach(type => {
            items[type.id] = (
                <>
                    {type.icon}
                    {type.title}
                </>
            );
        });
        return items;
    }

    function onCreatePanel(panel) {
        addToast('Successfully created a new panel', {
            appearance: 'success',
            autoDismiss: true
        });
        refetchPanels().then(() => {
            setPanelIdSelected(panel.id);
        });
    }

    useEffect(() => {
        if (!isQualtrics) {
            setIntegrationVendor(null);
            setExternalId(null);
            setCustomFields([]);
        }
    }, [isQualtrics]);

    return (
        <>
            <ActionButton title="New Screener" action={() => setShowModal(true)} keyLabel="N" mr={0} />
            <Portal>
                <NiceModal
                    isOpen={showModal}
                    shouldCloseOnOverlayClick
                    onRequestClose={() => setShowModal(false)}
                    style={{ content: { width: '600px', maxHeight: '90vh' } }}
                    title="Create a new screener"
                >
                    <form onSubmit={saveFormXHR}>
                        {panels !== null && panelIdSelected !== null && (
                            <Box mb={3}>
                                <Label>Associated Panel</Label>
                                <PanelSelect
                                    height="200px"
                                    width="100%"
                                    items={panels}
                                    value={panelIdSelected}
                                    onChange={panel_id => setPanelIdSelected(panel_id)}
                                    onCreatePanel={() => setShowModalCreatePanel(true)}
                                />
                            </Box>
                        )}

                        {screenerTypes.length > 1 && (
                            <>
                                <Label mt={3} htmlFor="description">
                                    Screener Tool
                                </Label>
                                <NiceSelect
                                    revealEllipsis={true}
                                    mimicSelectStyle={true}
                                    defaultValue={screenerType}
                                    positionRight="0px"
                                    items={screenerTypes}
                                    mapValueToLabel={getScreenerTypeValueMap()}
                                    onChange={setScreenerType}
                                />
                            </>
                        )}

                        {screenerType == SCREENER_TYPE.PANELFOX && (
                            <>
                                <Label mt={3} htmlFor="title">
                                    Internal Title
                                </Label>
                                <Input
                                    id="new-form-screener__title"
                                    type="text"
                                    name="title"
                                    placeholder="Online pre-screen"
                                    autoComplete="off"
                                    required
                                    autoFocus
                                    onChange={e => setTitle(e.target.value)}
                                />
                                <Label mt={3} htmlFor="description">
                                    Internal Description
                                </Label>
                                <Textarea
                                    id="new-form-screener__description"
                                    type="text"
                                    name="description"
                                    placeholder="Pre-screen for online shoppers"
                                    onChange={e => setDescription(e.target.value)}
                                />
                                <Label mt={3} htmlFor="description">
                                    Copy Screener
                                </Label>
                                <Combobox
                                    value={copyFormTypeId}
                                    items={getCopyDropdownItems}
                                    onChange={value => setCopyFormTypeId(value)}
                                />
                            </>
                        )}

                        {isQualtrics && (
                            <QualtricsSurveySelect
                                defaultValue={externalId}
                                onChange={survey => {
                                    if (survey) {
                                        setIntegrationVendor(SCREENER_INTEGRATION_VENDORS.QUALTRICS);
                                        setExternalId(survey.id);
                                        setTitle(`Qualtrics: ${survey.name}`);
                                        setDescription(`External Qualtrics Survey: ${survey.name}`);
                                    } else {
                                        setExternalId(null);
                                        setIntegrationVendor(null);
                                        setCustomFields([]);
                                    }
                                }}
                            />
                        )}

                        {screenerType !== SCREENER_TYPE.PANELFOX && (
                            <>
                                <Label mt={3}>Custom Fields</Label>
                                <ScreenerIntegrationCustomFields
                                    panelProperties={panelProperties}
                                    onChange={fields => {
                                        setCustomFields(fields);
                                    }}
                                />
                            </>
                        )}

                        <Box className="modal-actions">
                            <Button variant="secondary-gray" type="button" mr={3} onClick={() => setShowModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                mr={0}
                                type="submit"
                                variant="primary"
                                className="modal-primary"
                                disabled={createDisabled}
                            >
                                Create screener
                            </Button>
                        </Box>
                    </form>
                    <NiceModal
                        isOpen={showModalCreatePanel}
                        shouldCloseOnOverlayClick
                        onRequestClose={() => setShowModalCreatePanel(false)}
                        title={`Create new panel`}
                    >
                        <CreatePanel onCreate={onCreatePanel} onClose={() => setShowModalCreatePanel(false)} />
                    </NiceModal>
                </NiceModal>
            </Portal>
        </>
    );
}

export default NewScreener;
